import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';
import {
  BaseResponse,
  PostJSON,
  ErrorResponse,
  RequestJSON
} from '../../../libs/request';
import { DownloadBlobAsFile } from '../../../libs/file';
import { CardBlock, CardFrame } from '../../organisms/card';
import Modal from '../../organisms/modal';
import Pagination from '../../organisms/pagination';
import { SimpleSelect } from '../../organisms/select';
import { LoadingBlock } from '../../organisms/loading/progress';
import { Col, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import PatientTrackedTimeItem from './log-item';
import { GetCurrentTZ } from '../../../libs/time';
import { useTerms } from '../../../contexts/terms';
import TimeTrackedForm from './form';

interface PatientLogsProps {
  patientId: string;
}

interface TalksRequest {
  page: number;
  limit: number;
  patient: string;
  start: string;
  end: string;
  providerOnly: boolean;
}

interface TalksResponse extends BaseResponse {
  total: number;
  talks: Array<any>;
}

interface DeleteTalkRequest {
  id: string;
}

type DeleteTalkResponse = BaseResponse;

function isTalksResponse(
  talksResponse: TalksResponse | ErrorResponse
): talksResponse is TalksResponse {
  return (
    typeof (talksResponse as TalksResponse)?.total === 'number' &&
    Array.isArray((talksResponse as TalksResponse)?.talks)
  );
}

const PatientLogs = ({ patientId }: PatientLogsProps): JSX.Element => {
  const { terms } = useTerms();
  const [talks, setTalks] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [month, setMonth] = useState(moment().startOf('month'));
  const [loaded, setLoaded] = useState(false);
  const [isShowingEdit, setIsShowingEdit] = useState(false);
  const [talkForEditing, setTalkForEditing] = useState(null);
  const limit = 30;
  const start = month.clone().startOf('month').valueOf();
  const end = month.clone().endOf('month').valueOf();
  const tz = encodeURIComponent(GetCurrentTZ());

  useEffect(() => {
    fetchTalks();
  }, [month, page]);

  const fetchTalks = async () => {
    setLoaded(false);
    setTalks([]);
    const params = {
      page,
      limit,
      patient: patientId,
      start: month ? month.clone().startOf('month').format() : undefined,
      end: month ? month.clone().endOf('month').format() : undefined,
      providerOnly: true
    };
    const response = await PostJSON<TalksResponse, TalksRequest>({
      url: '/talk/data/',
      params
    });
    if (response.status === 'OK' && isTalksResponse(response)) {
      const totalOverLimit = Math.ceil(response.total / limit) || 1;
      setTalks(response.talks);
      setTotal(totalOverLimit);
      setLoaded(true);
    } else {
      if (response.message != null) {
        Modal.error({ title: 'Error', content: response.message });
      }
    }
  };

  const deleteTalk = async (talkId) => {
    Modal.confirm({
      title: 'Delete Entry',
      content:
        'Are you sure that you want to delete this entry? The action cannot be undone.',
      onOk: async () => {
        const response = await PostJSON<DeleteTalkResponse, DeleteTalkRequest>({
          url: '/talk/delete/',
          params: {
            id: talkId
          }
        });
        if (response.status === 'OK') {
          fetchTalks();
        }
      }
    });
  };

  const downloadReport = (type: 'pdf' | 'xlsx') => {
    const params = {
      patient: talks[0].patient.id,
      start,
      end,
      tz: GetCurrentTZ(),
      providerOnly: 1
    };
    const endpoint =
      type === 'pdf' ? '/talk/export_pdf/' : '/talk/export_xlsx/';

    RequestJSON<Blob>({
      url: endpoint,
      params,
      method: 'GET',
      isBlob: true
    })
      .then((response) => {
        if (response instanceof Blob) {
          const extension = type === 'pdf' ? 'pdf' : 'xlsx';
          const filename = `report_${
            talks[0].patient.id
          }_${moment().format('YYYYMMDD')}.${extension}`;
          DownloadBlobAsFile(response, filename);
        } else {
          if ((response as ErrorResponse).message != null) {
            Modal.error({
              title: 'Error',
              content: (response as ErrorResponse).message
            });
          }
        }
      })
      .catch((err) => {
        Modal.error({
          title: 'Error Exporting',
          content: err.message
        });
      });
  };

  const editTalk = () => {
    setIsShowingEdit(true);
  };

  const renderTimeLogs = () => {
    if (talks.length > 0) {
      return talks.map((talk) => (
        <PatientTrackedTimeItem
          key={talk.id}
          talk={talk}
          setTalkForEditing={setTalkForEditing}
          editTalk={editTalk}
          deleteTalk={deleteTalk}
        />
      ));
    } else if (loaded) {
      return (
        <h4 className="text-center">
          No time was tracked for this {terms.patient.default}
          {month ? <span> in {month.format('MMMM YYYY')}</span> : null}
        </h4>
      );
    } else {
      return <LoadingBlock />;
    }
  };

  return (
    <CardFrame>
      <CardBlock>
        <Row>
          <Col>
            <h4 className="mt-0">
              <SimpleSelect
                aria-label="Filter By Month"
                onChange={({ target: { value } }) => setMonth(moment(value))}
                value={month.toISOString()}
                fill={false}
                options={Array.from(Array(12).keys()).map((i) => {
                  const time = moment().startOf('month').subtract(i, 'months');
                  return {
                    value: time.toISOString(),
                    label: time.format('MMMM YYYY')
                  };
                })}
              />
            </h4>
          </Col>
          <Col>
            {talks.length > 0 ? (
              <div className="text-right">
                <a
                  className="bp5-button mr-2 hover-no-underline"
                  onClick={() => downloadReport('pdf')}
                  role="button"
                >
                  <Icon icon={faDownload} className="mr-2" />
                  <span className="text-small">Download as PDF</span>
                </a>
                <a
                  className="bp5-button hover-no-underline"
                  onClick={() => downloadReport('xlsx')}
                  role="button"
                >
                  <Icon icon={faDownload} className="mr-2" />
                  <span className="text-small">Download as XLSX</span>
                </a>
              </div>
            ) : null}
          </Col>
        </Row>
        <CardBlock className="text-small font-bold">
          <Row>
            <Col sm={6}>Duration</Col>
            <Col sm={6}>Date</Col>
            <Col sm={6}>Note</Col>
            <Col sm={6}>Actions</Col>
          </Row>
        </CardBlock>
        {renderTimeLogs()}
        <CardBlock>
          <Pagination total={total} onPageChange={setPage} />
        </CardBlock>
      </CardBlock>
      {isShowingEdit && talkForEditing ? (
        <TimeTrackedForm
          close={() => {
            setIsShowingEdit(false);
            fetchTalks();
          }}
          talk={talkForEditing}
        />
      ) : null}
    </CardFrame>
  );
};

export default PatientLogs;
